import { useRef, useEffect } from 'react';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';

const PARTICLE_COUNT = 75;
const PARTICLE_BASE_RADIUS = 1.5;
const FLICKER_SPEED = 0.05;
const MOVEMENT_SPEED = 0.5;

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  alpha: number;
  flickerOffset: number;
}

const Fireflies = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    const particles: Particle[] = [];

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const createParticles = () => {
      particles.length = 0; // Clear existing particles
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() * -1 - 0.2) * MOVEMENT_SPEED,
          radius: PARTICLE_BASE_RADIUS + Math.random() * 1.5,
          alpha: Math.random() * 0.5 + 0.2,
          flickerOffset: Math.random() * 2 * Math.PI,
        });
      }
    };

    resizeCanvas();
    createParticles();

    const handleResize = () => {
      resizeCanvas();
      createParticles();
    };

    window.addEventListener('resize', handleResize);

    if (prefersReducedMotion) {
      // Draw a static, subtle background if motion is disabled
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(173, 255, 47, ${p.alpha * 0.5})`; // Fainter static glow
        ctx.fill();
      });
      return;
    }

    const animate = (time: number) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((p) => {
        // Update position
        p.x += p.vx;
        p.y += p.vy;

        // Reset particle if it goes off-screen
        if (p.y < -p.radius) {
          p.y = canvas.height + p.radius;
          p.x = Math.random() * canvas.width;
        }
        if (p.x < -p.radius) p.x = canvas.width + p.radius;
        if (p.x > canvas.width + p.radius) p.x = -p.radius;

        // Calculate flicker
        const flicker = Math.sin(
          p.flickerOffset + time * FLICKER_SPEED * (Math.random() * 0.5 + 0.5)
        );
        const alpha = p.alpha * (0.5 + flicker * 0.5);

        // Draw particle with glow
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(173, 255, 47, ${alpha})`; // Green-Yellow glow
        ctx.shadowColor = 'rgba(173, 255, 47, 0.8)';
        ctx.shadowBlur = p.radius * 4;
        ctx.fill();
        ctx.shadowBlur = 0; // Reset shadow for next particle
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate(0);

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [prefersReducedMotion]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
      aria-hidden="true"
    />
  );
};

export default Fireflies;
